import { create } from "zustand";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export type ShareTab = "analytics" | "brief" | "creators";

export interface CreatorOptions {
  show_stats: boolean;
  show_fields: boolean;
  show_conversations: boolean;
}

export interface ShareConversationMessage {
  side: "creator" | "brand";
  sender_name: string | null;
  avatar: string | null;
  sent_at: string | null;
  content: string;
}

export interface SharePublicData {
  campaign: {
    campaign_name: string;
    platform: string;
    use_dub: boolean;
    stages?: any[];
  };
  tabs: ShareTab[];
  creatorOptions: CreatorOptions;
  campaignFields?: { name: string; type: string; description: string }[];
  creators?: any[];
  brief?: Record<string, any>;
  analytics?: { results: any[] };
}

interface ShareState {
  // ---- Public read (no auth) ----
  loading: boolean;
  notFound: boolean;
  error: string | null;
  data: SharePublicData | null;
  fetchShare: (token: string) => Promise<void>;

  // ---- Public conversation transcript (no auth) ----
  fetchSharedThread: (
    token: string,
    campaignCreatorId: string
  ) => Promise<{ subject: string | null; messages: ShareConversationMessage[] }>;

  // ---- Owner management (Clerk-authed) ----
  getShare: (
    campaignId: string,
    organizationId: string,
    token: string | null
  ) => Promise<{ token: string; tabs: ShareTab[]; creator_options: CreatorOptions } | null>;
  saveShare: (
    campaignId: string,
    organizationId: string,
    tabs: ShareTab[],
    creatorOptions: CreatorOptions,
    token: string | null
  ) => Promise<{ token: string; tabs: ShareTab[]; creator_options: CreatorOptions } | null>;
  deleteShare: (
    campaignId: string,
    organizationId: string,
    token: string | null
  ) => Promise<boolean>;
  /** Uplodio-members only: fabricate demo conversations from a brand website. */
  generateDemo: (
    campaignId: string,
    organizationId: string,
    websiteUrl: string,
    token: string | null
  ) => Promise<{ generated: number } | null>;
}

export const useShareStore = create<ShareState>((set) => ({
  loading: false,
  notFound: false,
  error: null,
  data: null,

  fetchShare: async (token: string) => {
    set({ loading: true, notFound: false, error: null, data: null });
    try {
      const res = await fetch(`${API_BASE_URL}/api/shares/${token}`, {
        method: "GET",
      });
      if (res.status === 404) {
        set({ loading: false, notFound: true });
        return;
      }
      if (!res.ok) throw new Error("Failed to load share");
      const data = await res.json();
      if (!data.success) {
        set({ loading: false, notFound: true });
        return;
      }
      set({
        loading: false,
        data: {
          campaign: data.campaign,
          tabs: data.tabs || [],
          creatorOptions: data.creator_options || {
            show_stats: false,
            show_fields: false,
            show_conversations: false,
          },
          campaignFields: data.campaign_fields,
          creators: data.creators,
          brief: data.brief,
          analytics: data.analytics,
        },
      });
    } catch (error) {
      set({ loading: false, error: (error as Error).message });
    }
  },

  fetchSharedThread: async (token, campaignCreatorId) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/shares/${token}/thread/${campaignCreatorId}`,
        { method: "GET" }
      );
      if (!res.ok) return { subject: null, messages: [] };
      const data = await res.json();
      if (!data.success) return { subject: null, messages: [] };
      return { subject: data.subject ?? null, messages: data.messages ?? [] };
    } catch {
      return { subject: null, messages: [] };
    }
  },

  getShare: async (campaignId, organizationId, token) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/shares/get`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          campaign_id: campaignId,
          organization_id: organizationId,
        }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data.share ?? null;
    } catch {
      return null;
    }
  },

  saveShare: async (campaignId, organizationId, tabs, creatorOptions, token) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/shares/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          campaign_id: campaignId,
          organization_id: organizationId,
          tabs,
          creator_options: creatorOptions,
        }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (!data.success) return null;
      return {
        token: data.token,
        tabs: data.tabs,
        creator_options: data.creator_options,
      };
    } catch {
      return null;
    }
  },

  deleteShare: async (campaignId, organizationId, token) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/shares/delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          campaign_id: campaignId,
          organization_id: organizationId,
        }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      return !!data.success;
    } catch {
      return false;
    }
  },

  generateDemo: async (campaignId, organizationId, websiteUrl, token) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/shares/generate-demo`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          campaign_id: campaignId,
          organization_id: organizationId,
          website_url: websiteUrl,
        }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (!data.success) return null;
      return { generated: data.generated };
    } catch {
      return null;
    }
  },
}));
